import { Component, AfterViewInit } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { BaseComponent } from '~/pages/base.component';
import { HttpService } from '~/core/services/http.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { FormatterService } from '~/core/services/formatter.service';
import { TabNavigationService } from '~/services/tab-navigation.service';
import { WellsApiService } from '~/services/wells-api.service';

@Component({
    selector: 'home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})

export class HomeComponent extends BaseComponent implements AfterViewInit {
    public title: string = "Wells Plug and Abandonment";

    // Tab Management
    public activeTab: 'wells' | 'requests' = 'wells';

    // Wells API Properties
    public wells: any[] = [];
    public filteredWells: any[] = [];
    public wellSearchQuery: string = "";
    public expandedWells: Set<string> = new Set(); // Track which wells have expanded schemas
    public wellSchemas: Map<string, any[]> = new Map(); // Store schemas for each well
    public loadingSchemas: Set<string> = new Set(); // Track which wells are loading schemas
    public expandedStages: Set<string> = new Set(); // Track which wells have expanded stages
    
    // Schema ordering configuration for ABU
    private readonly SCHEMA_ORDER_ABU = [
        'Well Header',
        'Wellbore',
        'Wellbore Section',
        'Geological Horizon',
        'Tubing',
        'Tubing Components',
        'Rod',
        'Rod Components',
        'Casing',
        'Casing Components',
        'Cement',
        'Cement Stage',
        'Perforation',
        'Other in Hole'
    ];
    
    // Mapping from API schema names to display names for ABU
    private readonly SCHEMA_NAME_MAP_ABU: { [key: string]: string } = {
        'cas': 'Casing',
        'casing': 'Casing',
        'cascomp': 'Casing Components',
        'casing_components': 'Casing Components',
        'casingcomponents': 'Casing Components',
        'cement': 'Cement',
        'cementstage': 'Cement Stage',
        'cement_stage': 'Cement Stage',
        'perforation': 'Perforation',
        'tubing': 'Tubing',
        'tubingcomp': 'Tubing Components',
        'tuibing_assembly': 'Tubing',
        'tubing_components': 'Tubing Components',
        'tubingcomponents': 'Tubing Components',
        'rod': 'Rod',
        'rod details': 'Rod',
        'rodcomp': 'Rod Components',
        'rod_components': 'Rod Components',
        'rodcomponents': 'Rod Components',
        'wellheader': 'Well Header',
        'well_header': 'Well Header',
        'wellbore': 'Wellbore',
        'wellboresz': 'Wellbore Section',
        'wellbore_sz': 'Wellbore Section',
        'wellboresection': 'Wellbore Section',
        'wellboreformation': 'Geological Horizon',
        'wellbore_formation': 'Geological Horizon',
        'otherinhole': 'Other in Hole',
        'other_in_hole': 'Other in Hole',
    };
    
    // Schema ordering configuration for RBU
    private readonly SCHEMA_ORDER_RBU = [
        'Well Header',
        'Wellbore',
        'Wellbore Section',
        'Geological Horizon',
        'Jobs',
        'Casing',
        'Casing Components',
        'Cement',
        'Cement Stage',
        'Perforation',
        'Other in Hole'
    ];
    
    // Mapping from API schema names to display names for RBU
    private readonly SCHEMA_NAME_MAP_RBU: { [key: string]: string } = {
        'cas': 'Casing',
        'casing': 'Casing',
        'cascomp': 'Casing Components',
        'casing_components': 'Casing Components',
        'casingcomponents': 'Casing Components',
        'cement': 'Cement',
        'cementstage': 'Cement Stage',
        'cement_stage': 'Cement Stage',
        'perforation': 'Perforation',
        'job': 'Jobs',
        'jobs': 'Jobs',
        'wellheader': 'Well Header',
        'well_header': 'Well Header',
        'wellbore': 'Wellbore',
        'wellboresz': 'Wellbore Section',
        'wellbore_sz': 'Wellbore Section',
        'wellboresection': 'Wellbore Section',
        'wellboreformation': 'Geological Horizon',
        'wellbore_formation': 'Geological Horizon',
        'otherinhole': 'Other in Hole',
        'other_in_hole': 'Other in Hole',
    };
    
    // Requests Properties
    public requests: any[] = [];
    public paginatedRequests: any[] = [];
    
    // Pagination Properties
    public paginatedWells: any[] = [];
    public currentPage: number = 1;
    public pageSize: number = 20;
    public totalPages: number = 1;
    public totalItems: number = 0;
    public hasNextPage: boolean = false;
    public hasPreviousPage: boolean = false;
    public Math = Math;
    public Array = Array;
    public isAuthenticated: boolean = false;
    
    // Sorting Properties
    public sortField: string = 'last_updated';
    public sortDirection: 'asc' | 'desc' = 'desc';
    public error: string = "";
    public notificationType: 'success' | 'error' | 'info' = 'error';
    public loading: boolean = false;
    public tableLoading: boolean = false;
    selectedBU$ = this.wellsApiService.selectedBU$;

    // Search debounce subject
    private searchSubject = new Subject<string>();

    // Upload Modal
    public showUploadModal: boolean = false;
    public isUploading: boolean = false;

    // Upload Type Selection Modal
    public showUploadTypeModal: boolean = false;
    public selectedUploadType: 'raw' | 'excel' | null = null;

    // ID Rec Selection Modal
    public showIdRecModal: boolean = false;
    public selectedWellForIdRec: any = null;
    public idRecOptions: any[] = [];
    public selectedIdRec: any = null;
    public loadingIdRec: boolean = false;

    private wellsSubscription!: Subscription;
    private routerEventsSubscription?: Subscription;
    private tabSubscriptions: Subscription = new Subscription();

    constructor(
        private httpService: HttpService,
        private appSettingsService: AppSettingsService,
        private formatterService: FormatterService,
        private route: ActivatedRoute,
        public router: Router,
        public tabNavigationService: TabNavigationService,
        private wellsApiService: WellsApiService
    ) {
        super();
        
        // Listen to router events to prevent page reload but still detect route changes
        this.routerEventsSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            const currentRoute = this.route.snapshot;
            if (currentRoute.routeConfig?.path === 'requests') {
                this.tabNavigationService.setActiveTab('requests');
            } else if (currentRoute.routeConfig?.path === 'wells') {
                this.tabNavigationService.setActiveTab('wells');
            }
        });
    }

    public async ngOnInit(): Promise<void> {
        await super.ngOnInit();
        
        // Set up debounced search (2000ms delay)
        this.tabSubscriptions.add(
            this.searchSubject.pipe(
                debounceTime(2000),
                distinctUntilChanged()
            ).subscribe(searchTerm => {
                this.currentPage = 1;
                this.fetchWells(1, this.pageSize, searchTerm || undefined);
            })
        );
        
        // Subscribe to tab changes
        this.tabSubscriptions.add(
            this.tabNavigationService.activeTab$.subscribe(tab => {
                if (tab !== this.activeTab) {
                    this.wellSearchQuery = "";
                    this.filteredWells = [...this.wells];
                    
                    this.activeTab = tab;
                    this.currentPage = 1;
                    this.updatePaginatedData();
                }
            })
        );
        
        // Subscribe to upload modal events
        this.tabSubscriptions.add(
            this.tabNavigationService.uploadModal$.subscribe(shouldOpen => {
                this.showUploadModal = shouldOpen;
            })
        );

        // Subscribe to upload type modal events
        this.tabSubscriptions.add(
            this.tabNavigationService.uploadTypeModal$.subscribe(shouldOpen => {
                this.showUploadTypeModal = shouldOpen;
                // Reset the selected upload type whenever modal opens
                if (shouldOpen) {
                    this.selectedUploadType = null;
                    this.tabNavigationService.resetSelectedUploadType();
                }
            })
        );

        // Subscribe to selected upload type changes
        this.tabSubscriptions.add(
            this.tabNavigationService.selectedUploadType$.subscribe(type => {
                this.selectedUploadType = type;
            })
        );

        // Refresh wells list when requested by shared navigation/upload flows
        this.tabSubscriptions.add(
            this.tabNavigationService.resetWells$.subscribe(shouldReset => {
                if (shouldReset) {
                    this.currentPage = 1;
                    this.fetchWells(1, this.pageSize, this.wellSearchQuery || undefined);
                }
            })
        );
        
        // Set initial tab based on current route
        const currentRoute = this.route.snapshot;
        if (currentRoute.routeConfig?.path === 'requests') {
            this.activeTab = 'requests';
        } else {
            this.activeTab = 'wells';
        }
        
        this.loadWells();
    }

    public ngAfterViewInit(): void {
        // Pagination setup
        this.updatePaginatedWells();
    }

    /**
     * Switch between Wells and Requests tabs
     */
    public switchTab(tab: 'wells' | 'requests'): void {
        // Navigate to the correct route for the tab
        if (tab === 'requests') {
            this.router.navigate(['/requests']);
        } else {
            this.router.navigate(['/wells']);
        }
        this.tabNavigationService.setActiveTab(tab);
    }

    public ngOnDestroy(): void {
        this.tabNavigationService.setInWellView(false);

        if (this.wellsSubscription) {
            this.wellsSubscription.unsubscribe();
        }
        this.tabSubscriptions.unsubscribe();
        if (this.routerEventsSubscription) {
            this.routerEventsSubscription.unsubscribe();
        }
        this.tabNavigationService.closeUploadModal();
        this.tabNavigationService.setActiveTab('wells');
    }

    //  Wells API Methods
    /**
     * Loads the list of wells from the API with pagination support.
     * Handles authentication and error management.
     */
    public loadWells(): void {
        this.clearError();
        this.loading = true;
        this.tableLoading = true;
        
        this.wellsSubscription = this.httpService.authenticateAndGetWells().subscribe({
            next: (response: any) => {
                // The response now contains wells array and pagination info
                this.wells = response.wells || [];
                this.paginatedWells = [...this.wells];
                
                // Update pagination info from server response
                if (response.pagination) {
                    this.currentPage = response.pagination.current_page || 1;
                    this.totalPages = response.pagination.total_pages || 1;
                    this.totalItems = response.pagination.total_items || this.wells.length;
                    this.hasNextPage = response.pagination.has_next || false;
                    this.hasPreviousPage = response.pagination.has_previous || false;
                } else {
                    this.totalPages = 1;
                    this.totalItems = this.wells.length;
                }
                
                this.filteredWells = [...this.wells];
                this.isAuthenticated = true;
                this.loading = false;
                this.tableLoading = false;
            },
            error: (error: any) => {
                this.error = `Failed to load wells: ${error.error?.detail || error.message || 'Unknown error'}`;
                this.wells = [];
                this.filteredWells = [];
                this.paginatedWells = [];
                this.isAuthenticated = false;
                this.loading = false;
                this.tableLoading = false;
            }
        });
    }

    /**
     * Fetches wells from API with current pagination and search params
     */
    public fetchWells(page: number = this.currentPage, pageSize: number = this.pageSize, search?: string): void {
        this.clearError();
        this.tableLoading = true;
        
        const searchTerm = search !== undefined ? search : this.wellSearchQuery;
        
        this.wellsSubscription = this.httpService.getWellsList(page, pageSize, searchTerm || undefined).subscribe({
            next: (response: any) => {
                this.wells = response.wells || [];
                this.paginatedWells = [...this.wells];
                
                // Update pagination info from server response
                if (response.pagination) {
                    this.currentPage = response.pagination.current_page || 1;
                    this.totalPages = response.pagination.total_pages || 1;
                    this.totalItems = response.pagination.total_items || this.wells.length;
                    this.hasNextPage = response.pagination.has_next || false;
                    this.hasPreviousPage = response.pagination.has_previous || false;
                } else {
                    this.totalPages = 1;
                    this.totalItems = this.wells.length;
                }
                
                this.filteredWells = [...this.wells];
                this.tableLoading = false;
            },
            error: (error: any) => {
                this.error = `Failed to load wells: ${error.error?.detail || error.message || 'Unknown error'}`;
                this.wells = [];
                this.filteredWells = [];
                this.paginatedWells = [];
                this.tableLoading = false;
            }
        });
    }

    public clearError(): void {
        this.error = "";
    }

    // Upload Modal Methods
    public openUploadTypeModal(): void {
        // Just open the modal - subscription will handle reset
        this.tabNavigationService.openUploadTypeModal();
    }

    public closeUploadTypeModal(): void {
        this.showUploadTypeModal = false;
        this.selectedUploadType = null;
        this.tabNavigationService.closeUploadTypeModal();
    }

    public selectUploadType(type: 'raw' | 'excel'): void {
        this.selectedUploadType = type;
        this.tabNavigationService.setSelectedUploadType(type);
    }

    public proceedWithUploadType(): void {
        if (!this.selectedUploadType) return;
        
        // Save the selected upload type to service
        this.tabNavigationService.setSelectedUploadType(this.selectedUploadType);
        
        // Close the upload type modal
        this.closeUploadTypeModal();
        
        // Navigate to upload route
        this.router.navigate(['/upload']);
    }

    public openUploadModal(): void {
        this.tabNavigationService.openUploadModal();
    }

    public closeUploadModal(): void {
        this.tabNavigationService.closeUploadModal();
    }

    public onFileSelected(file: File): void {
        console.log('File selected:', file.name);
        
        // Validate file type
        const validExtensions = ['.xlsx', '.xls'];
        const fileName = file.name.toLowerCase();
        const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));
        
        if (!isValidFile) {
            this.showNotification('Invalid file type. Only Excel files (.xlsx, .xls) are allowed.', 'error');
            return;
        }
        
        // Start upload
        this.isUploading = true;
        this.showNotification(`Uploading "${file.name}"...`, 'info');
        
        this.httpService.uploadExcelFile(file).subscribe({
            next: (response) => {
                this.isUploading = false;
                
                // Start countdown timer and keep modal open
                let countdown = 5;
                
                // Show combined success message with timer
                const successMessage = `Upload of "${file.name}" is successful! It'll take some time to process the excel and load data. Please be patient. Navigating to wells page in ${countdown} seconds...`;
                this.showNotification(successMessage, 'success');
                
                const countdownInterval = setInterval(() => {
                    countdown--;
                    if (countdown > 0) {
                        // Update the snackbar with new countdown
                        const updatedMessage = `Upload of "${file.name}" is successful! It'll take some time to process the excel and load data. Please be patient. Navigating to wells page in ${countdown} ${countdown === 1 ? 'second' : 'seconds'}...`;
                        this.showNotification(updatedMessage, 'success');
                    } else {
                        clearInterval(countdownInterval);
                        // Close modal and navigate to wells tab
                        this.closeUploadModal();
                        this.activeTab = 'wells';
                        this.tabNavigationService.setActiveTab('wells');
                        this.fetchWells();
                    }
                }, 1000);
            },
            error: (error) => {
                console.error('Upload error in component:', error);
                this.isUploading = false;
                const errorMessage = error?.message || 'Upload failed. Please try again.';
                console.log('Showing error notification:', errorMessage);
                this.showNotification(errorMessage, 'error');
                // Don't close modal on error so user can try again
            }
        });
    }

    public onWellSelect(well: any): void {
        // Get the selected BU
        const bu = this.wellsApiService.getSelectedBU();
        
        // Extract the well ID (handles both new and legacy formats)
        const wellIda = well.well_id || well.name || well;
        
        // Store stages_completed in the navigation service
        // Handle all possible types: array, string "Pending", or falsy values
        let stagesCompleted: string[] = [];
        if (Array.isArray(well.stages_completed)) {
            stagesCompleted = well.stages_completed;
        } else if (well.stages_completed && typeof well.stages_completed === 'string') {
            // If it's a string like "Pending", keep it as an array with that value
            stagesCompleted = [well.stages_completed];
        }
        // For 0, null, undefined, empty string - use empty array
        this.tabNavigationService.setStagesCompleted(stagesCompleted);
        
        // For ABU, directly navigate to well detail page without modal
        if (bu === 'ABU') {
            this.router.navigate(['/wells', wellIda]);
            return;
        }
        
        // For RBU, fetch ID rec data from API and show modal
        if (bu === 'RBU') {
            // Store the selected well
            this.selectedWellForIdRec = wellIda;
            
            // Show modal and start loading
            this.showIdRecModal = true;
            this.loadingIdRec = true;
            this.idRecOptions = [];
            
            // Fetch wellbore ID rec data
            this.wellsApiService.getWellboreIdRec(wellIda, bu).subscribe({
                next: (response: any) => {
                    // Map API response to idRecOptions format
                    if (response.wellbores && Array.isArray(response.wellbores)) {
                        this.idRecOptions = response.wellbores.map((wellbore: any) => ({
                            id: wellbore.idrec,
                            name: wellbore.des || 'Unknown',
                            description: wellbore.wellboreida ? `Wellbore IDA: ${wellbore.wellboreida}` : 'No wellbore IDA',
                            wellboreida: wellbore.wellboreida,
                            idrecparent: wellbore.idrecparent
                        }));
                    } else {
                        this.idRecOptions = [];
                        this.showNotification('No wellbore schemes found for this well.', 'info');
                    }
                    this.loadingIdRec = false;
                },
                error: (error: any) => {
                    console.error('Error fetching wellbore ID rec:', error);
                    this.loadingIdRec = false;
                    this.showNotification(`Failed to load wellbore schemes: ${error.error?.detail || error.message || 'Unknown error'}`, 'error');
                    // Keep modal open so user can close it manually
                }
            });
        }
    }

    /**
     * Handle ID Rec card selection
     */
    public selectIdRec(idRec: any): void {
        this.selectedIdRec = idRec;
    }

    /**
     * Proceed with the selected ID Rec
     */
    public proceedWithIdRec(): void {
        if (!this.selectedIdRec) {
            this.showNotification('Please select an ID Rec option', 'error');
            return;
        }

        // Store selected ID Rec and well information in localStorage
        const idRecData = {
            idrec: this.selectedIdRec.id,
            name: this.selectedIdRec.name,
            description: this.selectedIdRec.description,
            wellboreida: this.selectedIdRec.wellboreida,
            idrecparent: this.selectedIdRec.idrecparent,
            well_name: this.selectedWellForIdRec,
            well_ida: this.selectedWellForIdRec,
            timestamp: new Date().toISOString()
        };
        
        localStorage.setItem('selectedIdRec', JSON.stringify(idRecData));
        
        // Close modal
        this.showIdRecModal = false;
        
        // Navigate to well detail page (well is already the name string)
        this.router.navigate(['/wells', this.selectedWellForIdRec]);
        
        // Reset selection
        this.selectedIdRec = null;
        this.selectedWellForIdRec = null;
    }

    /**
     * Close ID Rec selection modal
     */
    public closeIdRecModal(): void {
        this.showIdRecModal = false;
        this.selectedIdRec = null;
        this.selectedWellForIdRec = null;
    }

    // ===================================
    // Pagination Methods
    // ===================================
    
    /**
     * Update paginated requests based on current page
     */
    private updatePaginatedRequests(): void {
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.paginatedRequests = this.requests.slice(startIndex, endIndex);
    }
    
    /**
     * Navigate to specific page
     */
    public goToPage(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.currentPage = page;
        if (this.activeTab === 'wells') {
            this.fetchWells(page, this.pageSize, this.wellSearchQuery);
        } else {
            this.updatePaginatedRequests();
        }
    }
    
    /**
     * Go to next page
     */
    public nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            if (this.activeTab === 'wells') {
                this.fetchWells(this.currentPage, this.pageSize, this.wellSearchQuery);
            } else {
                this.updatePaginatedRequests();
            }
        }
    }
    
    /**
     * Go to previous page
     */
    public prevPage(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            if (this.activeTab === 'wells') {
                this.fetchWells(this.currentPage, this.pageSize, this.wellSearchQuery);
            } else {
                this.updatePaginatedRequests();
            }
        }
    }
    
    /**
     * Get array of page numbers for pagination display
     */
    public getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        
        if (this.totalPages <= maxPagesToShow) {
            for (let i = 1; i <= this.totalPages; i++) {
                pages.push(i);
            }
        } else {
            let startPage = Math.max(1, this.currentPage - 2);
            let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
            
            if (endPage - startPage < maxPagesToShow - 1) {
                startPage = Math.max(1, endPage - maxPagesToShow + 1);
            }
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }
        
        return pages;
    }
    
    /**
     * Handle page size change
     */
    public onPageSizeChange(): void {
        this.currentPage = 1; // Reset to first page when changing page size
        if (this.activeTab === 'wells') {
            this.fetchWells(1, this.pageSize, this.wellSearchQuery);
        } else {
            this.updatePaginatedRequests();
        }
    }

    /**
     * Searches wells based on the current search query (server-side)
     */
    public searchWells(): void {
        // Use debounced search to avoid too many API calls
        this.searchSubject.next(this.wellSearchQuery);
    }
    
    /**
     * Clears the search query and resets to show all wells
     */
    public clearSearch(): void {
        this.wellSearchQuery = '';
        this.currentPage = 1;
        // Fetch wells without search filter immediately (no debounce for clear)
        this.fetchWells(1, this.pageSize, '');
    }
    
    /**
     * Updates paginated data based on active tab
     */
    public updatePaginatedData(): void {
        if (this.activeTab === 'wells') {
            // For wells, server-side pagination is used, so just call fetchWells
            this.fetchWells(this.currentPage, this.pageSize, this.wellSearchQuery);
        } else {
            this.updatePaginatedRequests();
        }
    }

    /**
     * Updates the paginated wells array based on current page (for local data only)
     */
    public updatePaginatedWells(): void {
        // This is now mainly used for local filtering
        // Server-side pagination is handled by fetchWells
        this.totalPages = Math.ceil(this.filteredWells.length / this.pageSize) || 1;
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.paginatedWells = this.filteredWells.slice(startIndex, endIndex);
    }
    
    /**
     * Simple notification system (can be replaced with a proper notification service)
     */
    private showNotification(message: string, type: 'success' | 'error' | 'info'): void {
        // Set the notification message and type
        this.error = message;
        this.notificationType = type;
        
        // Clear error message after 5 seconds if it's a success or info
        if (type === 'success' || type === 'info') {
            setTimeout(() => {
                if (this.error === message) {
                    this.error = '';
                }
            }, 5000);
        }
    }

    /**
     * Toggle schemas expansion for a well
     */
    public toggleSchemas(well: any, event: Event): void {
        event.stopPropagation(); // Prevent row click
        
        const wellId = well.well_id;
        
        if (this.expandedWells.has(wellId)) {
            // Collapse
            this.expandedWells.delete(wellId);
        } else {
            // Expand - fetch schemas if not already loaded
            this.expandedWells.add(wellId);
            
            if (!this.wellSchemas.has(wellId) && !this.loadingSchemas.has(wellId)) {
                this.fetchSchemas(wellId);
            }
        }
    }

    /**
     * Fetch schemas for a specific well
     */
    private fetchSchemas(wellId: string): void {
        this.loadingSchemas.add(wellId);
        
        // Get the BU to determine which API endpoint to use
        const bu = this.wellsApiService.getSelectedBU();
        
        console.log('Fetching schemas for well:', wellId, 'BU:', bu);
        
        // Call API with BU parameter - the service will handle RBU vs ABU logic
        this.wellsApiService.getSchemaList(wellId, bu).subscribe({
            next: (schemas: any[]) => {
                console.log('Raw API response for', wellId, ':', schemas);
                console.log('Response type:', typeof schemas, 'Is array:', Array.isArray(schemas));
                
                // Handle different response formats
                let schemaList: any[] = [];
                if (Array.isArray(schemas)) {
                    schemaList = schemas;
                } else if (schemas && typeof schemas === 'object') {
                    // If response is an object, try to extract array from common properties
                    const schemaObj = schemas as any;
                    if ('schemas' in schemaObj) {
                        schemaList = schemaObj.schemas;
                    } else if ('data' in schemaObj) {
                        schemaList = schemaObj.data;
                    } else {
                        // Convert object to array
                        schemaList = Object.values(schemaObj);
                    }
                }
                
                console.log('Processed schema list:', schemaList);
                
                // Process and order schemas using the same logic as well-detail page
                const orderedSchemas = this.processAndOrderSchemas(schemaList, bu);
                
                this.wellSchemas.set(wellId, orderedSchemas);
                this.loadingSchemas.delete(wellId);
            },
            error: (error: any) => {
                console.error(`Failed to load schemas for well ${wellId}:`, error);
                this.showNotification(`Failed to load schemas: ${error.error?.detail || error.message}`, 'error');
                this.loadingSchemas.delete(wellId);
                this.expandedWells.delete(wellId); // Collapse on error
            }
        });
    }
    
    /**
     * Process and order schemas using the same logic as well-detail page
     */
    private processAndOrderSchemas(schemas: any[], bu: string): any[] {
        // Select appropriate schema order and name map based on BU
        const SCHEMA_ORDER = bu === 'RBU' ? this.SCHEMA_ORDER_RBU : this.SCHEMA_ORDER_ABU;
        const SCHEMA_NAME_MAP = bu === 'RBU' ? this.SCHEMA_NAME_MAP_RBU : this.SCHEMA_NAME_MAP_ABU;
        
        // Normalize API schema names and map to display names
        const normalizedSchemas = schemas.map(schema => {
            const apiName = typeof schema === 'string' ? schema : (schema.schema_type || schema.name || schema.type || schema.id || '');
            const normalizedApiName = apiName.toLowerCase().replace(/[\s_]+/g, '');
            const displayName = SCHEMA_NAME_MAP[normalizedApiName] || this.formatSchemaName(apiName);
            
            return {
                apiName: apiName,
                displayName: displayName
            };
        });
        
        // Create a set of available display names from API response
        const availableDisplayNames = new Set<string>(normalizedSchemas.map(s => s.displayName));
        
        // Build ordered schema list
        const orderedSchemas = SCHEMA_ORDER
            .filter(displayName => availableDisplayNames.has(displayName))
            .map(displayName => {
                const schema = normalizedSchemas.find(s => s.displayName === displayName);
                return schema;
            })
            .filter(s => s !== undefined);
        
        return orderedSchemas;
    }

    /**
     * Check if a well has expanded schemas
     */
    public isSchemaExpanded(wellId: string): boolean {
        return this.expandedWells.has(wellId);
    }

    /**
     * Get schemas for a well
     */
    public getSchemasForWell(wellId: string): any[] {
        return this.wellSchemas.get(wellId) || [];
    }

    /**
     * Check if schemas are loading for a well
     */
    public isSchemasLoading(wellId: string): boolean {
        return this.loadingSchemas.has(wellId);
    }

    /**
     * Format stages completed for display
     */
    public formatStagesCompleted(stages: any): string {
        if (Array.isArray(stages)) {
            return stages.join(', ');
        }
        return stages || 'Pending';
    }

    /**
     * Format date for display
     */
    public formatDate(dateString: string | null): string {
        if (!dateString) {
            return 'N/A';
        }
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Get schema display name
     */
    public getSchemaName(schema: any): string {
        if (typeof schema === 'string') {
            return schema;
        }
        if (schema && typeof schema === 'object') {
            return schema.displayName || schema.schema_type || schema.name || schema.type || schema.id || 'Schema';
        }
        return 'Schema';
    }
    
    /**
     * Format schema name from API name
     */
    private formatSchemaName(name: string): string {
        return name
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    }

    /**
     * Toggle stages expansion for a well
     */
    public toggleStages(well: any, event: Event): void {
        event.stopPropagation(); // Prevent row click
        
        const wellId = well.well_id;
        
        if (this.expandedStages.has(wellId)) {
            // Collapse
            this.expandedStages.delete(wellId);
        } else {
            // Expand
            this.expandedStages.add(wellId);
        }
    }

    /**
     * Check if a well has expanded stages
     */
    public isStagesExpanded(wellId: string): boolean {
        return this.expandedStages.has(wellId);
    }

    /**
     * Get stages count for display
     */
    public getStagesCount(stages: any): number {
        if (Array.isArray(stages)) {
            return stages.length;
        }
        return 0;
    }

    /**
     * Get stages array for display
     */
    public getStagesArray(stages: any): string[] {
        if (Array.isArray(stages)) {
            if (stages.length === 0) {
                return ['Pending'];
            }
            // Normalize stage names using the same logic as schemas
            const bu = this.wellsApiService.getSelectedBU();
            const SCHEMA_NAME_MAP = bu === 'RBU' ? this.SCHEMA_NAME_MAP_RBU : this.SCHEMA_NAME_MAP_ABU;
            
            return stages.map(stage => {
                const normalizedStageName = stage.toLowerCase().replace(/[\s_]+/g, '');
                return SCHEMA_NAME_MAP[normalizedStageName] || this.formatSchemaName(stage);
            });
        }
        return ['Pending'];
    }
    
    /**
     * Check if stages is pending (empty or no stages)
     */
    public isStagesPending(stages: any): boolean {
        if (Array.isArray(stages)) {
            return stages.length === 0;
        }
        // Check if stages is the string "Pending" or falsy
        if (typeof stages === 'string' && stages.toLowerCase() === 'pending') {
            return true;
        }
        return !stages;
    }

    /**
     * Sort wells by column
     */
    public sortByColumn(column: string): void {
        // If already sorting by this column, toggle direction
        if (this.sortField === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            // New column selected, default to ascending
            this.sortField = column;
            this.sortDirection = 'asc';
        }

        // Sort the paginated wells
        this.sortWells();
    }

    /**
     * Apply sorting to wells based on current sort field and direction
     */
    private sortWells(): void {
        // Separate wells with dates from wells without dates
        const wellsWithDates: any[] = [];
        const wellsWithoutDates: any[] = [];

        this.paginatedWells.forEach(well => {
            if (well[this.sortField] && well[this.sortField].toString().trim() !== '') {
                wellsWithDates.push(well);
            } else {
                wellsWithoutDates.push(well);
            }
        });

        // Sort only the wells with dates
        wellsWithDates.sort((a, b) => {
            let valueA = a[this.sortField];
            let valueB = b[this.sortField];

            // For dates, parse them for proper comparison
            if (this.sortField === 'last_updated') {
                valueA = new Date(valueA).getTime();
                valueB = new Date(valueB).getTime();
            }

            // Numeric comparison for dates
            if (this.sortDirection === 'asc') {
                return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
            } else {
                return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
            }
        });

        // Combine sorted wells with dates first, then wells without dates at the bottom
        this.paginatedWells = [...wellsWithDates, ...wellsWithoutDates];
    }

    /**
     * Get sort indicator for column
     */
    public getSortIndicator(column: string): string {
        if (this.sortField !== column) {
            return '';
        }
        return this.sortDirection === 'asc' ? '▲' : '▼';
    }
}
